import { Box, Typography, Link } from "@mui/material";
import { useTranslation } from "react-i18next";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

interface Address {
  street?: string;
  city?: string;
  zip_code?: string;
  country?: string;
}

interface AddressDisplayProps {
  address: Address | null | undefined;
  title: string;
}

export const AddressDisplay = ({ address, title }: AddressDisplayProps) => {
  const { t } = useTranslation();

  const buildGoogleMapsUrl = (addr: Address): string => {
    const addressParts = [
      addr.street,
      addr.city,
      addr.zip_code,
      addr.country,
    ]
      .filter(Boolean)
      .join(", ");

    if (!addressParts) {
      return "";
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}`;
  };

  const googleMapsUrl = address ? buildGoogleMapsUrl(address) : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {googleMapsUrl && (
          <Link
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              textDecoration: "none",
              color: "primary.main",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <OpenInNewIcon fontSize="small" />
            <Typography variant="body2" component="span">
              {t("View on Google Maps")}
            </Typography>
          </Link>
        )}
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle1">
          <strong>{t("Street")}:</strong> {address?.street || "-"}
        </Typography>
        <Typography variant="subtitle1">
          <strong>{t("City")}:</strong> {address?.city || "-"}
        </Typography>
        <Typography variant="subtitle1">
          <strong>{t("Zip Code")}:</strong> {address?.zip_code || "-"}
        </Typography>
        <Typography variant="subtitle1">
          <strong>{t("Country")}:</strong> {address?.country || "-"}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {[
              address?.street,
              address?.city,
              address?.zip_code,
              address?.country,
            ]
              .filter(Boolean)
              .join(", ")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
